import { useEffect, useRef } from "react";
import { usePaneStore } from "../state/store-context";
import "./bbox-legend.css";

interface TierRow {
  label: string;
  color: string;
}

const TIERS: TierRow[] = [
  { label: "High ≥ 85%", color: "rgba(34, 197, 94, 0.6)" },
  { label: "Medium 65–84%", color: "rgba(234, 179, 8, 0.6)" },
  { label: "Low < 65%", color: "rgba(239, 68, 68, 0.7)" },
  { label: "No OCR data", color: "rgba(150, 150, 150, 0.6)" },
];

/**
 * R-134 / Phase 1.5 WU-4 — docked Boxes Legend popover.
 *
 * Was: a floating overlay anchored to the canvas (`.bbox-legend`) that
 * obscured the form image, with an inline open/close toggle inside the
 * BboxOverlayToolbar.
 *
 * Is: a header-button-anchored popover that matches the visual treatment
 * of Audit Trail / Detach Panel in the station header. Trigger lives in
 * the header (see `BboxLegendHeaderButton` below), the popover renders
 * inline beneath it, and the close button still works.
 *
 * The store flag `showBboxLegend` continues to be the source of truth so
 * the existing keyboard shortcut (`L`) and the bbox-toggle hook keep
 * working untouched.
 */
export function BboxLegend() {
  const visible = usePaneStore((s) => s.showBboxLegend);
  const hide = usePaneStore((s) => s.setBboxLegendVisible);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside-click / Escape — matches the popover pattern of
  // sibling station chrome (Audit Trail menu, OverrideDialog, etc.).
  useEffect(() => {
    if (!visible) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        // Only dismiss when click is outside both the popover AND the
        // toggle button — the button has data-bbox-legend-trigger so we
        // can leave its click handler to toggle without re-opening.
        const trigger = (e.target as Element).closest?.(
          "[data-bbox-legend-trigger]",
        );
        if (!trigger) hide(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") hide(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [visible, hide]);

  if (!visible) return null;

  return (
    <aside
      className="bbox-legend bbox-legend--popover"
      aria-label="Bounding box color legend"
      role="dialog"
      ref={ref}
    >
      <div className="bbox-legend__header">
        <span className="bbox-legend__title">Overlay colors</span>
        <button
          type="button"
          onClick={() => hide(false)}
          aria-label="Close legend"
          className="bbox-legend__close"
        >
          ×
        </button>
      </div>
      <ul className="bbox-legend__list">
        {TIERS.map((t) => (
          <li key={t.label} className="bbox-legend__item">
            <span className="bbox-legend__swatch" style={{ background: t.color }} />
            <span className="bbox-legend__label">{t.label}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/**
 * Header trigger for the docked Boxes Legend popover. Matches the
 * `vs-detach-btn` look used by Audit Trail / Detach Panel in the station
 * header so the three chrome controls read as one group.
 */
export function BboxLegendHeaderButton() {
  const visible = usePaneStore((s) => s.showBboxLegend);
  const toggle = usePaneStore((s) => s.toggleBboxLegend);
  return (
    <button
      type="button"
      className="vs-detach-btn"
      aria-pressed={visible}
      aria-label="Boxes Legend"
      data-bbox-legend-trigger
      data-testid="bbox-legend-toggle"
      onClick={toggle}
      title="Show overlay color legend (L)"
    >
      {visible ? "Hide Legend" : "Legend"}
    </button>
  );
}
